import { toast } from "@assetwatch/ui";
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { mlApiErrorStatus } from "./api/mlApi";
import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      errorMessage?: string;
    };
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // React Query retries 3× by default, so one rejected ML API request became
      // four. A 4xx means the request itself is wrong — retrying can't fix it,
      // it just multiplies load on 29s-budget queries and delays the error.
      retry: (failureCount, error) => {
        const status = mlApiErrorStatus(error);

        if (status !== undefined && status >= 400 && status < 500) {
          return false;
        }

        return failureCount < 2;
      },
      // ML API queries are expensive enough that silently re-running one on
      // alt-tab is worse than showing slightly stale data.
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (_error, query) => {
      const { errorMessage } = query.meta ?? {};

      if (errorMessage) {
        toast.error(errorMessage, { icon: true, onDismiss: () => {} });
      }
    },
  }),
});

export const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  defaultPendingMs: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
